<script setup lang="ts">
import ChevronRight from '@assets/icons/chevron-right.svg';
import CloseIcon from '@assets/icons/close-round.svg';
import UserProfileIcon from '@assets/icons/user-profile.svg';
import type { MenuItem } from '@models/menu';
import { onBeforeUnmount, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    items: MenuItem[];
    iconColor?: 'white' | 'dark';
    variant?: 'desktop' | 'mobile';
  }>(),
  { iconColor: 'dark', variant: 'desktop' },
);

const isOpen = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const dropdownPosition = ref({ top: 0, right: 0 });

const updatePosition = () => {
  if (triggerRef.value) {
    const rect = triggerRef.value.getBoundingClientRect();
    const gap = props.variant === 'mobile' ? 0 : 8;
    dropdownPosition.value = {
      top: rect.bottom + gap,
      right: window.innerWidth - rect.right,
    };
  }
};

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    updatePosition();
  }
};

const close = () => {
  isOpen.value = false;
};

const handleScroll = () => close();

watch(isOpen, (open) => {
  if (open) {
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', handleScroll, true);
  } else {
    window.removeEventListener('resize', updatePosition);
    window.removeEventListener('scroll', handleScroll, true);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePosition);
  window.removeEventListener('scroll', handleScroll, true);
});
</script>

<template>
  <div
    class="site-header-user-menu relative"
    :class="props.variant === 'desktop' ? 'hidden lg:flex' : 'flex lg:hidden'"
  >
    <button
      ref="triggerRef"
      type="button"
      class="flex size-36 items-center justify-center rounded-full transition-colors hover:opacity-80"
      :class="iconColor === 'white' ? 'text-white' : 'text-deep-blue'"
      aria-label="Open user menu"
      aria-haspopup="true"
      :aria-expanded="isOpen"
      @click="toggle"
    >
      <UserProfileIcon class="size-24" />
    </button>

    <Teleport to="body">
      <Transition name="dropdown-fade">
        <div
          v-if="isOpen"
          class="fixed inset-0 z-[9999]"
        >
          <div
            v-if="props.variant === 'mobile'"
            class="fixed inset-0 bg-black/40"
            aria-hidden="true"
            @click="close"
          />
          <div
            v-click-outside="close"
            :class="[
              'site-header-user-menu__dropdown soft-shadow fixed w-[370px] max-w-[calc(100vw-32px)] rounded-[30px] bg-white px-24',
              props.variant === 'desktop' ? 'pb-16 pt-24 -mr-16 -mt-40' : 'pb-16 pt-24',
            ]"
            :style="{
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`,
              left: 'auto',
            }"
          >
            <div class="-mt-8 flex justify-end">
              <button
                type="button"
                class="flex size-32 items-center justify-center text-off-black-500 transition-colors hover:text-copper-300"
                aria-label="Close menu"
                @click="close"
              >
                <CloseIcon class="size-32" />
              </button>
            </div>
            <ul class="mt-16">
              <li
                v-for="item in items"
                :key="item.path ?? item.name"
                class="border-b-2 border-b-warm-grey-30 last:border-0"
              >
                <a
                  :href="item.path"
                  class="flex w-full flex-row items-center gap-24 py-20 transition-colors hover:opacity-80"
                  @click="close"
                >
                  <div class="flex size-80 shrink-0 justify-center rounded-[20px] bg-copper-300 p-12">
                    <img
                      :src="item.img"
                      :alt="item.name"
                      class="size-54 object-contain"
                    >
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-16">
                      <span class="text-18 font-bold text-off-black-500">{{ item.name }}</span>
                      <ChevronRight class="-mt-2 ml-auto size-24 shrink-0 text-copper-500" />
                    </div>
                    <div
                      v-if="item.desc"
                      class="text-neutral-500 mt-10 pr-32 text-14"
                    >
                      {{ item.desc }}
                    </div>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s ease;
}
.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
}
</style>
